import {
  cloneElement,
  ComponentType,
  forwardRef,
  isValidElement,
  ReactNode,
  useEffect,
} from 'react';

import { Backdrop, BackdropProps, Paper, PaperProps } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { motion, MotionProps } from 'motion/react';

import styles from './ExpandableCardDialog.module.scss';
import { Dialog } from '@/components/ui/Dialog';

const MotionPaper = motion.create(Paper);
const MotionBackdrop = motion.create(Backdrop);

/* Suppresses MUI's built-in Dialog fade/slide so Motion's layoutId animation is the sole transition.
 * The ref must be forwarded to a real DOM element so MUI's FocusTrap sets rootRef.current — without it
 * FocusTrap bails on every effect and focus never enters the dialog, breaking Escape key handling.
 * onEnter signals Modal to set exited=false; Fade does the same via cloneElement.
 */
const NoTransition = forwardRef<
  HTMLDivElement,
  TransitionProps & { children: ReactNode }
>(({ children, in: inProp, onEnter, onEntered }, ref) => {
  useEffect(() => {
    if (inProp) {
      onEnter?.(document.body, false);
      onEntered?.(document.body, false);
    }
  }, [inProp, onEnter, onEntered]);

  if (!isValidElement<{ ref?: typeof ref }>(children)) return null;
  return cloneElement(children, { ref });
});
NoTransition.displayName = 'NoTransition';

export interface ExpandableCardDialogProps {
  cardId: string;
  onClose: () => void;
  children: ReactNode;
}

export const ExpandableCardDialog = ({
  cardId,
  onClose,
  children,
}: ExpandableCardDialogProps) => {
  return (
    <Dialog
      open
      onClose={onClose}
      PaperComponent={MotionPaper as ComponentType<PaperProps>}
      maxWidth="sm"
      fullWidth
      slots={{
        transition: NoTransition,
        backdrop: MotionBackdrop,
      }}
      slotProps={{
        paper: {
          className: styles.DialogPaper,
          layoutId: cardId,
        } as PaperProps & MotionProps,
        backdrop: {
          initial: { opacity: 0 },
          animate: { opacity: 0.7 },
          transition: { ease: 'easeOut', duration: 0.15 },
          exit: { opacity: 0 },
          sx: {
            backgroundColor: 'var(--color-black-20)',
          },
        } as BackdropProps & MotionProps,
      }}
    >
      {children}
    </Dialog>
  );
};
